import * as React from 'react'
// libs
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { arrayRemoveAll, FieldArray, getFormValues, InjectedFormProps, reduxForm } from 'redux-form'
// components
import { Button, BUTTON_LAYOUTS } from 'components/UI'
import { ReduxInput, ReduxSelect, ReduxSwitch } from 'components/FormElements/ReduxForm'
// view components
import DeleteButton from './components/DeleteButton'
import renderConditions from './components/Conditions'
import renderTransactions from './components/Transactions'
import EmailPreviewModal from 'components/Modals/EmailPreviewModal'
import FieldRecurrenceModal from 'components/Modals/ReccurenceSettingsModal'
// constants
import { Forms, Modals, OBJECTS_OPTIONS, PROCESS_TYPE_OPTIONS } from 'constants/index'
// hooks
import { useModalData } from 'hooks/index'
// views
import { BottomPaper as BottomPaperOrigin } from 'themes/views/settings'
// utils
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { required } from 'utils/index'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'

// styled
const Form = styled('form')`
  display: flex;
  position: relative;
  flex-direction: column;
  height: calc(100% - 121px);
  overflow: auto;

  > div {
    padding: 16px 24px 0 16px;
  }
`

const Wrapper = styled('div')`
  margin-bottom: 16px;

  > div {
    margin-bottom: 16px;
  }
`

// @ts-ignore
const BottomPaper = styled(BottomPaperOrigin)`
  && {
    margin: 0;
    position: fixed;
    bottom: 0;
    right: 0;
    width: 800px;
    z-index: 100;

    > div:first-child {
      margin-right: 16px;
    }
  }
`

// types
interface IIncomingProps {
  hideModal: () => void
  deleteSystemProcess: any
}

interface IFormData {
  active: boolean
  name: string
  processType: IOption
  object: IOption
}

interface IOption {
  label: string
  value: string
}

type IFormProps = InjectedFormProps<IFormData>

const SystemProcessCreateForm: React.FC<IFormProps & IIncomingProps> = ({
  handleSubmit,
  deleteSystemProcess,
  initialValues,
  reset
}) => {
  const { isRiskTemplate } = React.useContext(RiskTemplateAPIContext)

  const {
    hideModal,
    modalData: { isEdit }
  } = useModalData({ modalType: Modals.SYSTEM_PROCESS_CREATE_MODAL })

  const { formValues } = useSelector((state) => ({
    formValues: getFormValues(Forms.SYSTEM_PROCESS_CREATE_FORM)(state)
  }))

  const dispatch = useDispatch()

  // methods
  const onSelectChange =
    (fieldName: string) =>
    ({ value }) => {
      const isObjectChanged = fieldName === 'object' && value !== get(initialValues, 'object.value')
      const isProcessTypeChanged = fieldName === 'processType' && value !== get(initialValues, 'processType.value')

      if (isObjectChanged || isProcessTypeChanged) {
        dispatch(arrayRemoveAll(Forms.SYSTEM_PROCESS_CREATE_FORM, 'conditions'))
        dispatch(arrayRemoveAll(Forms.SYSTEM_PROCESS_CREATE_FORM, 'transactions'))
      } else {
        reset()
      }
    }

  return (
    <Form onSubmit={handleSubmit}>
      {isEdit && <DeleteButton removeField={deleteSystemProcess} iconSize={'24px'} />}

      <Wrapper>
        <ReduxSwitch label={'Active'} name={'active'} reverse />
        <ReduxInput label={'Name'} name={'name'} validate={required} />
        <ReduxSelect
          label={'Process Type'}
          name={'processType'}
          onSelectChange={onSelectChange('processType')}
          options={PROCESS_TYPE_OPTIONS}
          validate={required}
        />
        <ReduxSelect
          label={'Object'}
          name={'object'}
          onSelectChange={onSelectChange('object')}
          options={OBJECTS_OPTIONS}
          validate={required}
          isDisabled={isRiskTemplate}
        />
        <FieldArray<any> name={'conditions'} component={renderConditions} />
        <FieldArray<any> name={'transactions'} component={renderTransactions} />

        <EmailPreviewModal />
        <FieldRecurrenceModal />
      </Wrapper>

      <BottomPaper square>
        <Button
          type={'submit'}
          layout={BUTTON_LAYOUTS.PURPLE}
          disabled={
            isRiskTemplate
              ? isEmpty(get(formValues, 'transactions', []))
              : isEmpty(get(formValues, 'conditions', [])) || isEmpty(get(formValues, 'transactions', []))
          }>
          {isEdit ? 'Save' : 'Create'}
        </Button>
        <Button layout={BUTTON_LAYOUTS.WHITE} type={'button'} onClick={hideModal}>
          Cancel
        </Button>
      </BottomPaper>
    </Form>
  )
}

export default reduxForm<any, any>({
  form: Forms.SYSTEM_PROCESS_CREATE_FORM,
  enableReinitialize: true
})(SystemProcessCreateForm)
